import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/sidebar/Sidebar';
import NavbarAdmin from '../components/navbar/NavbarAdmin';
import { getAllCategories } from '../services/categoryService';
import {
  createSubcategory,
  getAllSubcategories,
  deleteSubcategory,
  updateSubcategory,
  importSubcategoriesFromExcel
} from '../services/subcategoryService';
import swal from 'sweetalert';
import { FiUpload } from 'react-icons/fi';

const SUBCATEGORIES_KEY = 'cached_subcategories';

const SubCategoriesScreen = () => {
  const { slug } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [subCategoryName, setSubCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState({ inserted: [], skipped: [] });
  const [isRefreshing, setIsRefreshing] = useState(false);


  const fetchData = async (showRefreshLoader = false) => {
    try {
      if (showRefreshLoader) setIsRefreshing(true);

      const [catRes, subRes] = await Promise.all([
        getAllCategories(),
        getAllSubcategories(),
      ]);

      const newSubcategories = subRes.data;
      const oldSubcategoriesRaw = localStorage.getItem(SUBCATEGORIES_KEY);
      const oldSubcategories = oldSubcategoriesRaw ? JSON.parse(oldSubcategoriesRaw) : [];

      const hasChanged = JSON.stringify(oldSubcategories) !== JSON.stringify(newSubcategories);

      if (hasChanged) {
        localStorage.setItem(SUBCATEGORIES_KEY, JSON.stringify(newSubcategories));
        setSubcategories(newSubcategories);
      }

      setCategories(catRes.data);
    } catch {
      swal("Erreur", "Impossible de charger les données", "error");
    } finally {
      if (showRefreshLoader) {
        setTimeout(() => setIsRefreshing(false), 1000);
      }
    }
  };


  const handleAddOrEdit = async () => {
    try {
      if (editMode) {
        await updateSubcategory(editingId, subCategoryName, selectedCategories);
        swal("Modifié", "Sous-catégorie mise à jour.", "success");
      } else {
        await createSubcategory(subCategoryName, selectedCategories);
        swal("Ajoutée", "Sous-catégorie ajoutée avec succès.", "success");
      }
      localStorage.removeItem(SUBCATEGORIES_KEY);
      await fetchData();
      resetModal();
    } catch {
      swal("Erreur!", "Nom déjà utilisé ou erreur serveur.", "error");
    }
  };

const handleExcelImport = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  setIsImporting(true);
  setImportStatus({ inserted: [], skipped: [] });

  try {
    const res = await importSubcategoriesFromExcel(file);
    const { inserted, skipped } = res.data;

    setImportStatus({
      inserted: inserted || [],
      skipped: skipped || [],
    });


    await fetchData();
  } catch (err) {
    swal("Erreur", "Échec de l'importation.", "error");
  } finally {
    setTimeout(() => {
      setIsImporting(false);
      setImportStatus({ inserted: [], skipped: [] });
    }, 5000);

  }
};




  const handleDelete = async (id) => {
    const confirm = await swal({
      title: "Supprimer cette sous-catégorie ?",
      text: "Cette action est irréversible.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (confirm) {
      try {
        await deleteSubcategory(id);
        swal("Supprimée", "Sous-catégorie supprimée.", "success");
        localStorage.removeItem(SUBCATEGORIES_KEY);
        await fetchData();
      } catch {
        swal("Erreur", "Échec de la suppression.", "error");
      }
    }
  };

  const handleEdit = (sc) => {
    setEditMode(true);
    setEditingId(sc._id);
    setSubCategoryName(sc.name);
    setSelectedCategories(
      sc.categories?.map(cat => typeof cat === 'object' ? cat._id : cat) || []
    );
    setShowModal(true);
  };

  const resetModal = () => {
    setEditMode(false);
    setEditingId(null);
    setSubCategoryName('');
    setSelectedCategories([]);
    setShowModal(false);
  };

useEffect(() => {
  const cached = localStorage.getItem(SUBCATEGORIES_KEY);
  if (cached) {
    setSubcategories(JSON.parse(cached));
  }

  fetchData(); 
}, []);

  const filtered = subcategories.filter((sc) =>
    sc.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="flex min-h-screen">
      {isRefreshing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <img src="/assets/loading.gif" alt="Chargement" className="mx-auto w-24 h-24 mb-4" />
            <h2 className="text-lg font-bold mb-2">Rafraîchissement en cours...</h2>
            <p className="text-gray-700">Merci de patienter un instant</p>
          </div>
        </div>
      )}

      {isImporting && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <img src="/assets/loading.gif" alt="Chargement" className="mx-auto w-16 h-16 mb-4" />
            <h2 className="text-lg font-bold mb-2">Import en cours...</h2>
            <p className="text-gray-700">Merci de patienter un instant</p>
          </div>
        </div>
      )}

      <div className="md:hidden fixed top-0 left-0 right-0 z-50">
        <NavbarAdmin slug={slug} />
      </div>
      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>
      <main className="flex-1 md:ml-64 p-6 mt-16 md:mt-0">
        <div className="container mx-auto mt-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <h1 className="text-2xl font-bold text-paragaia">Sous-catégories</h1>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowModal(true)}
                className="bg-paragaia text-white px-4 py-2 rounded hover:bg-paragaiaLight"
              >
                Ajouter
              </button>
              <button
                onClick={() => fetchData(true)}
                className="bg-pink-200 text-gray-700 px-4 py-2 rounded hover:bg-pink-300"
              >
                Rafraîchir
              </button>

              <label
                htmlFor="excel-upload"
                className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm md:text-base flex items-center justify-center gap-2 whitespace-nowrap"
                style={{ height: '40px' }}
              >
                <FiUpload className="text-lg" />
                <span className="hidden sm:inline">Import Excel</span>
              </label>
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => {
                  handleExcelImport(e);
                  e.target.value = null;
                }} 
                className="hidden"
              />

            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <input
              type="text"
              placeholder="Rechercher une sous-catégorie..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-1/2 p-3 border rounded"
            />
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="p-2 border rounded"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Afficher {n}</option>
              ))}
            </select>
          </div>

          {/* MODAL */}
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <div className="bg-white p-6 rounded-lg w-full max-w-md">
                <h2 className="text-xl font-bold mb-4">
                  {editMode ? "Modifier" : "Ajouter"} une sous-catégorie
                </h2>
                <div className="space-y-2 mb-4">
                  {selectedCategories.map((catId, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <select
                        value={catId}
                        onChange={(e) => {
                          const updated = [...selectedCategories];
                          updated[index] = e.target.value;
                          setSelectedCategories(updated);
                        }}
                        className="w-full p-3 border rounded"
                      >
                        <option value="">Sélectionner une catégorie</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                      {selectedCategories.length > 1 && (
                        <button
                          onClick={() => {
                            const updated = [...selectedCategories];
                            updated.splice(index, 1);
                            setSelectedCategories(updated);
                          }}
                          className="text-red-600 px-2 py-1 rounded border border-red-300 hover:bg-red-100"
                        >
                          –
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setSelectedCategories([...selectedCategories, ''])}
                    className="mt-2 text-sm text-blue-600 hover:underline"
                  >
                    + Ajouter une catégorie
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Nom de la sous-catégorie"
                  value={subCategoryName}
                  onChange={(e) => setSubCategoryName(e.target.value)}
                  className="w-full mb-4 p-3 border rounded"
                />
                <div className="flex justify-end space-x-2">
                  <button onClick={resetModal} className="px-4 py-2 border rounded">Annuler</button>
                  <button onClick={handleAddOrEdit} className="bg-paragaia text-white px-4 py-2 rounded">
                    {editMode ? "Modifier" : "Ajouter"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* DESKTOP TABLE */}
          <div className="bg-white rounded-lg shadow overflow-x-auto hidden md:block">
            <table className="w-full text-left">
              <thead className="bg-paragaia text-white">
                <tr>
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Nom</th>
                  <th className="py-3 px-4">Catégorie</th>
                  <th className="py-3 px-4">Créé le</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((sub, index) => (
                  <tr key={sub._id} className="border-b hover:bg-gray-100">
                    <td className="py-3 px-4">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="py-3 px-4">{sub.name}</td>
                    <td className="py-3 px-4">
                    {sub.categories && sub.categories.length > 0
                      ? sub.categories
                          .map(cat =>
                            typeof cat === 'object' && cat !== null
                              ? (cat.name || cat._id)
                              : cat
                          )
                          .filter(Boolean)
                          .join(', ')
                      : '–'}

                    </td>
                    <td className="py-3 px-4">{new Date(sub.createdAt).toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() => handleEdit(sub)}
                          className="w-24 px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(sub._id)}
                          className="w-24 px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>

                  </tr>
                ))}
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-500">
                      Aucune sous-catégorie trouvée.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARD VIEW */}
          <div className="md:hidden space-y-4">
            {paginated.map((sub, index) => (
              <div key={sub._id} className="bg-white p-4 rounded-lg shadow">
                <div className="mb-2">
                  <p className="font-semibold text-gray-800">{sub.name}</p>
                  <p className="text-sm text-gray-500">
                  {sub.categories && sub.categories.length > 0
                    ? sub.categories
                        .map(cat =>
                          typeof cat === 'object' && cat !== null
                            ? (cat.name || cat._id)
                            : cat
                        )
                        .filter(Boolean)
                        .join(', ')
                    : '–'}


                  </p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(sub.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={() => handleEdit(sub)}
                    className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(sub._id)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
            {paginated.length === 0 && (
              <p className="text-center text-gray-500 py-6">Aucune sous-catégorie trouvée.</p>
            )}
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-1 items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className={`px-3 py-1 rounded border ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'}`}
              >
                &lt;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (page === 1 || page === totalPages) return true;
                  if (page >= currentPage - 2 && page <= currentPage + 2) return true;
                  return false;
                })
                .map((page, i, arr) => {
                  const prevPage = arr[i - 1];
                  const isGap = prevPage && page - prevPage > 1;

                  return (
                    <React.Fragment key={page}>
                      {isGap && (
                        <span className="px-2 text-gray-500 select-none">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded border ${
                          currentPage === page
                            ? 'bg-paragaia text-white'
                            : 'bg-white text-paragaia hover:bg-paragaiaLight'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className={`px-3 py-1 rounded border ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'}`}
              >
                &gt;
              </button>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default SubCategoriesScreen;
